import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, GripVertical, Star, CalendarPlus, Trash2, X, Eye, EyeOff, ListTodo, CornerDownRight, Flag, Plus } from "lucide-react";
import { format, isPast, endOfDay, startOfDay } from "date-fns";
import { formatDateInput } from "@/lib/dateUtils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export type SubTask = {
  id: string;
  taskId: string;
  tenantId: string;
  userId: string;
  text: string;
  completed: boolean;
  dueDate: string | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  isStarred?: boolean;
  milestoneId?: string | null;
  parentSubTaskId?: string | null;
  dependsOnSubTaskId?: string | null;
};

export type TaskMilestoneLite = {
  id: string;
  title: string;
  completed?: boolean;
  displayOrder?: number;
};

// ─── ScheduleTaskDialog ──────────────────────────────────────────────────────

export function ScheduleTaskDialog({ open, onOpenChange, task, taskId: taskIdOverride }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  task: any;
  taskId?: string | null;
}) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState("09:00");
  const [duration, setDuration] = useState("60");
  const [repeatIfIncomplete, setRepeatIfIncomplete] = useState(false);
  const { toast } = useToast();

  const { data: dayEvents = [] } = useQuery<any[]>({
    queryKey: ["/api/calendar", selectedDate?.toISOString().split("T")[0]],
    queryFn: async () => {
      if (!selectedDate) return [];
      const res = await fetch(`/api/calendar?startDate=${startOfDay(selectedDate).toISOString()}&endDate=${endOfDay(selectedDate).toISOString()}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedDate,
  });

  const resolvedTaskId = taskIdOverride === undefined ? task?.id : (taskIdOverride ?? undefined);

  const createMutation = useMutation({
    mutationFn: async () => {
      const [hours, mins] = selectedTime.split(":").map(Number);
      const start = new Date(selectedDate!);
      start.setHours(hours, mins, 0, 0);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + Number(duration));
      const event = {
        title: task.title,
        ...(resolvedTaskId ? { taskId: resolvedTaskId } : {}),
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        eventType: "task",
        color: "#6366f1",
        repeatIfIncomplete,
      };
      await apiRequest("POST", "/api/calendar", event);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      toast({ title: "Added to Calendar", description: `"${task?.title}" scheduled.` });
      setSelectedDate(undefined); setRepeatIfIncomplete(false);
      onOpenChange(false);
    },
    onError: () => toast({ title: "Error", description: "Failed to schedule task.", variant: "destructive" }),
  });

  const DURATIONS = [
    { value: "15", label: "15 min" }, { value: "30", label: "30 min" }, { value: "45", label: "45 min" },
    { value: "60", label: "1 hour" }, { value: "90", label: "1.5 hours" }, { value: "120", label: "2 hours" },
    { value: "180", label: "3 hours" }, { value: "240", label: "4 hours" },
  ];

  const handleClose = (o: boolean) => {
    if (!o) { setSelectedDate(undefined); setRepeatIfIncomplete(false); }
    onOpenChange(o);
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-indigo-500" />
            Add to Calendar
          </DialogTitle>
          <DialogDescription>Schedule &quot;{task.title}&quot;</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Start Date</Label>
              <CalendarPicker mode="single" selected={selectedDate} onSelect={setSelectedDate} className="border rounded-md p-2" />
            </div>
            {selectedDate && (
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {format(selectedDate, "EEEE, MMM d")} — Existing Events
                </Label>
                <div className="border rounded-md divide-y max-h-[260px] overflow-y-auto">
                  {dayEvents.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-3">No events scheduled on this day</p>
                  ) : dayEvents.map((ev: any) => (
                    <div key={ev.id} className="flex items-center gap-2 px-3 py-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color || "#3b82f6" }} />
                      <span className="text-xs font-medium text-muted-foreground w-14 flex-shrink-0">
                        {ev.allDay ? "All day" : format(new Date(ev.startTime), "h:mm a")}
                      </span>
                      <span className="text-xs truncate">{ev.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {selectedDate && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Time</Label>
                  <Input type="time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">How Long</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DURATIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Push to next day if incomplete</div>
                    <div className="text-xs text-muted-foreground">If not marked done by end of day, automatically moves to the next day</div>
                  </div>
                  <Switch checked={repeatIfIncomplete} onCheckedChange={setRepeatIfIncomplete} />
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
          <Button onClick={() => createMutation.mutate()}
            disabled={!selectedDate || createMutation.isPending}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {createMutation.isPending ? "Scheduling…" : "Add to Calendar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── SortableInlineSubTaskItem ───────────────────────────────────────────────

export function SortableInlineSubTaskItem({ subtask, onToggle, onDateChange, onTextChange, onStar, onDelete, subtaskIsOverdue, readOnly, depth = 0, connector, blockedByText, dragDisabled, dependencyOptions, onSetDependency }: {
  subtask: SubTask;
  onToggle: (id: string) => void;
  onDateChange: (id: string, date: string | null) => void;
  onTextChange: (id: string, text: string) => void;
  onStar: (id: string) => void;
  /** Called when the user clicks the delete button on the row. */
  onDelete?: (id: string) => void;
  subtaskIsOverdue: boolean;
  /** True when the parent task is shared view-only; the star becomes non-interactive. */
  readOnly?: boolean;
  /** Tree depth for the structured (branching) view; indents the row. */
  depth?: number;
  /** Branch connector kind for the structured view. */
  connector?: "child" | "dependency";
  /** When set, this row is waiting on an incomplete dependency; shows a chip. */
  blockedByText?: string;
  /** Hide the drag affordance (structured view has no drag reorder). */
  dragDisabled?: boolean;
  /** Sibling sub-tasks this row may depend on; enables the dependency picker. */
  dependencyOptions?: { id: string; text: string }[];
  /** Sets (or clears, with null) this row's dependency. */
  onSetDependency?: (id: string, dependsOnId: string | null) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: subtask.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const [depOpen, setDepOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [localText, setLocalText] = useState(subtask.text ?? "");
  const selectedDate = subtask.dueDate ? new Date(subtask.dueDate) : undefined;

  useEffect(() => { setLocalText(subtask.text ?? ""); }, [subtask.text]);

  const saveText = () => {
    const trimmed = (localText ?? "").trim();
    if (trimmed && trimmed !== subtask.text) onTextChange(subtask.id, trimmed);
    else setLocalText(subtask.text ?? "");
  };

  return (
    <>
      <div ref={setNodeRef} style={{ ...style, marginLeft: depth * 22 }} {...attributes} className="flex items-center gap-2 pl-8 pr-2 py-1 rounded hover:bg-muted/30 group/st">
        {connector && (
          <CornerDownRight
            className={`h-3 w-3 flex-shrink-0 -ml-5 ${connector === "dependency" ? "text-indigo-400" : "text-muted-foreground/60"}`}
            aria-hidden="true"
          />
        )}
        {readOnly || dragDisabled ? (
          <div className="opacity-0 flex-shrink-0" aria-hidden="true">
            <GripVertical className="h-3 w-3 text-muted-foreground" />
          </div>
        ) : (
          <div {...listeners} tabIndex={0} data-testid={`drag-handle-subtask-${subtask.id}`} className="touch-none cursor-grab active:cursor-grabbing opacity-0 group-hover/st:opacity-100 [@media(pointer:coarse)]:opacity-100 focus-visible:opacity-100 transition-opacity flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <GripVertical className="h-3 w-3 text-muted-foreground" />
          </div>
        )}
        <div onClick={(e) => e.stopPropagation()} title={readOnly ? "View-only shared task — you can't edit sub-tasks" : undefined}>
          <Checkbox
            checked={subtask.completed}
            disabled={readOnly}
            onCheckedChange={() => { if (!readOnly) onToggle(subtask.id); }}
            className="h-3.5 w-3.5 flex-shrink-0"
          />
        </div>
        <input
          className={`flex-1 text-sm min-w-0 bg-transparent border-0 outline-none rounded transition-all ${readOnly ? "cursor-default" : "focus:bg-muted/50 focus:px-1 cursor-text"} ${subtask.completed ? "line-through text-muted-foreground" : ""}`}
          value={localText}
          readOnly={readOnly}
          title={readOnly ? "View-only shared task — you can't edit sub-tasks" : undefined}
          onChange={(e) => { if (!readOnly) setLocalText(e.target.value); }}
          onBlur={() => { if (!readOnly) saveText(); }}
          onKeyDown={(e) => {
            if (readOnly) return;
            if (e.key === "Enter") { e.preventDefault(); saveText(); (e.target as HTMLInputElement).blur(); }
            if (e.key === "Escape") { setLocalText(subtask.text); (e.target as HTMLInputElement).blur(); }
          }}
          onClick={(e) => e.stopPropagation()}
        />
        {!readOnly && onSetDependency && (dependencyOptions?.length || subtask.dependsOnSubTaskId) ? (
          <Popover open={depOpen} onOpenChange={setDepOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`h-6 w-6 flex-shrink-0 ${subtask.dependsOnSubTaskId ? "text-indigo-500 hover:text-indigo-600" : "text-muted-foreground/50 hover:text-indigo-500 opacity-0 group-hover/st:opacity-100 focus-visible:opacity-100 transition-opacity"}`}
                onClick={(e) => e.stopPropagation()}
                title={subtask.dependsOnSubTaskId ? "Change dependency" : "Depends on another sub-task…"}
                data-testid={`button-set-dependency-${subtask.id}`}
              >
                <CornerDownRight className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-1" onClick={(e) => e.stopPropagation()}>
              <p className="text-xs font-medium text-muted-foreground px-2 pt-1.5 pb-1">Depends on…</p>
              <div className="max-h-52 overflow-y-auto">
                {(dependencyOptions ?? []).map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted truncate ${subtask.dependsOnSubTaskId === opt.id ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-medium" : ""}`}
                    onClick={() => { onSetDependency(subtask.id, opt.id); setDepOpen(false); }}
                    data-testid={`option-dependency-${subtask.id}-${opt.id}`}
                  >
                    {opt.text}
                  </button>
                ))}
                {(dependencyOptions ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground px-2 py-1.5">No other sub-tasks at this level.</p>
                )}
              </div>
              {subtask.dependsOnSubTaskId && (
                <div className="border-t mt-1 pt-1">
                  <button
                    type="button"
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted text-muted-foreground"
                    onClick={() => { onSetDependency(subtask.id, null); setDepOpen(false); }}
                    data-testid={`option-clear-dependency-${subtask.id}`}
                  >
                    <X className="h-3 w-3 inline mr-1" />Remove dependency
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        ) : null}
        {blockedByText && !subtask.completed && (
          <span
            className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 whitespace-nowrap"
            title={blockedByText}
            data-testid={`badge-blocked-subtask-${subtask.id}`}
          >
            waiting
          </span>
        )}
        {/* View-only rows without a due date show no chip at all (matches
            StarredSubTaskDatePicker) — no point presenting a control the user
            can never use. */}
        {readOnly && !subtask.dueDate ? null : (
        <Popover open={readOnly ? false : dateOpen} onOpenChange={(open) => { if (!readOnly) setDateOpen(open); }}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={readOnly}
              title={readOnly ? "View-only shared task — you can't edit sub-tasks" : undefined}
              className={`h-6 px-1.5 text-xs gap-1 flex-shrink-0 ${readOnly ? "cursor-not-allowed " : ""}${subtaskIsOverdue && !subtask.completed ? "text-red-500" : "text-muted-foreground"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Calendar className="h-3 w-3" />
              {subtask.dueDate ? format(new Date(subtask.dueDate), "MMM d") : <span className="opacity-40">date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" onClick={(e) => e.stopPropagation()}>
            <div className="px-3 pt-3 pb-1">
              <p className="text-xs font-medium text-muted-foreground">Due Date</p>
            </div>
            <CalendarPicker
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                onDateChange(subtask.id, date ? formatDateInput(date) : null);
                setDateOpen(false);
              }}
              className="p-2"
            />
            {subtask.dueDate && (
              <div className="border-t px-2 pb-2">
                <Button variant="ghost" size="sm" className="w-full text-xs h-7 mt-1"
                  onClick={(e) => { e.stopPropagation(); onDateChange(subtask.id, null); setDateOpen(false); }}>
                  <X className="h-3 w-3 mr-1" />Clear date
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        )}
        <Button
          variant="ghost"
          size="icon"
          disabled={readOnly}
          className={`h-6 w-6 flex-shrink-0 ${readOnly ? "cursor-not-allowed " : ""}${subtask.isStarred ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground/50 hover:text-amber-500"}`}
          onClick={(e) => { e.stopPropagation(); if (!readOnly) onStar(subtask.id); }}
          title={readOnly ? "View-only shared task — you can't edit sub-tasks" : subtask.isStarred ? "Remove star" : "Star subtask"}
          data-testid={`button-star-subtask-${subtask.id}`}
        >
          <Star className="h-3 w-3" style={subtask.isStarred ? { fill: "#f59e0b" } : {}} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950 flex-shrink-0 opacity-0 group-hover/st:opacity-100 [@media(pointer:coarse)]:opacity-100 focus-visible:opacity-100 transition-opacity"
          onClick={(e) => { e.stopPropagation(); setSchedOpen(true); }}
          title="Add to Calendar"
          data-testid={`button-add-to-calendar-subtask-${subtask.id}`}
        >
          <CalendarPlus className="h-3 w-3" />
        </Button>
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0 opacity-0 group-hover/st:opacity-100 [@media(pointer:coarse)]:opacity-100 focus-visible:opacity-100 transition-opacity"
            onClick={(e) => { e.stopPropagation(); onDelete(subtask.id); }}
            title="Delete sub-task"
            data-testid={`button-delete-subtask-${subtask.id}`}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
      <ScheduleTaskDialog
        task={{ id: subtask.id, title: subtask.text }}
        taskId={subtask.taskId}
        open={schedOpen}
        onOpenChange={setSchedOpen}
      />
    </>
  );
}

// ─── StarredSubTasksSection ──────────────────────────────────────────────────
// Flat list of starred sub-tasks whose parent task is not itself starred.
// Shown under the "Starred" quick filter so users see the sub-task directly
// without an extra click through the parent task.

function StarredSubTaskDatePicker({ subTask, readOnly, onDateChange }: {
  subTask: SubTask;
  readOnly: boolean;
  onDateChange: (id: string, date: string | null) => void;
}) {
  const [dateOpen, setDateOpen] = useState(false);
  const overdue = !subTask.completed && !!subTask.dueDate && isPast(new Date(subTask.dueDate));
  // View-only rows without a due date show no chip at all (matches mobile's
  // `(!stReadOnly || !!subTask.dueDate)` condition) — no point presenting a
  // control the user can never use.
  if (readOnly && !subTask.dueDate) return null;
  return (
    <Popover open={readOnly ? false : dateOpen} onOpenChange={(open) => { if (!readOnly) setDateOpen(open); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={readOnly}
          title={readOnly ? "View-only shared task — you can't edit sub-tasks" : "Set due date"}
          className={`h-6 px-1.5 text-xs gap-1 flex-shrink-0 ${readOnly ? "cursor-not-allowed " : ""}${overdue ? "text-red-500" : "text-muted-foreground"}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`button-starred-subtask-date-${subTask.id}`}
        >
          <Calendar className="h-3 w-3" />
          {subTask.dueDate ? format(new Date(subTask.dueDate), "MMM d") : <span className="opacity-40">date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" onClick={(e) => e.stopPropagation()}>
        <div className="px-3 pt-3 pb-1">
          <p className="text-xs font-medium text-muted-foreground">Due Date</p>
        </div>
        <CalendarPicker
          mode="single"
          selected={subTask.dueDate ? new Date(subTask.dueDate) : undefined}
          onSelect={(date) => {
            onDateChange(subTask.id, date ? formatDateInput(date) : null);
            setDateOpen(false);
          }}
          className="p-2"
        />
        {subTask.dueDate && (
          <div className="border-t px-2 pb-2">
            <Button variant="ghost" size="sm" className="w-full text-xs h-7 mt-1"
              onClick={(e) => { e.stopPropagation(); onDateChange(subTask.id, null); setDateOpen(false); }}>
              <X className="h-3 w-3 mr-1" />Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function StarredSubTasksSection({ items, onToggle, onStar, onOpenParent, onDateChange }: {
  items: { subTask: SubTask; parentTask: { id: string; title: string; isShared?: boolean; sharedPermission?: string } }[];
  onToggle: (id: string) => void;
  onStar: (id: string) => void;
  onOpenParent: (task: any) => void;
  onDateChange: (id: string, date: string | null) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4">
      <div className="border rounded-lg bg-card p-3" data-testid="section-starred-subtasks">
        <div className="flex items-center gap-2 mb-2">
          <Star className="h-4 w-4 text-amber-500" style={{ fill: "#f59e0b" }} />
          <h3 className="text-sm font-semibold">Starred sub-tasks</h3>
        </div>
        <div className="space-y-1">
          {items.map(({ subTask, parentTask }) => {
            const readOnly = !!parentTask.isShared && parentTask.sharedPermission !== "edit";
            return (
              <div key={subTask.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/30" data-testid={`starred-subtask-row-${subTask.id}`}>
                <Checkbox
                  checked={subTask.completed}
                  disabled={readOnly}
                  onCheckedChange={() => { if (!readOnly) onToggle(subTask.id); }}
                  className="h-3.5 w-3.5 flex-shrink-0"
                  data-testid={`checkbox-starred-subtask-${subTask.id}`}
                />
                <span className={`flex-1 text-sm min-w-0 truncate ${subTask.completed ? "line-through text-muted-foreground" : ""}`}>
                  {subTask.text}
                </span>
                <StarredSubTaskDatePicker subTask={subTask} readOnly={readOnly} onDateChange={onDateChange} />
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline flex-shrink-0 max-w-[10rem] truncate"
                  onClick={() => onOpenParent(parentTask)}
                  title={`Open task: ${parentTask.title}`}
                  data-testid={`button-starred-subtask-parent-${subTask.id}`}
                >
                  {parentTask.title}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={readOnly}
                  className={`h-6 w-6 flex-shrink-0 ${readOnly ? "cursor-not-allowed " : ""}text-amber-500 hover:text-amber-600`}
                  onClick={() => { if (!readOnly) onStar(subTask.id); }}
                  title={readOnly ? "View-only shared task — you can't edit sub-tasks" : "Remove star"}
                  data-testid={`button-star-subtask-starred-${subTask.id}`}
                >
                  <Star className="h-3 w-3" style={{ fill: "#f59e0b" }} />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── AddMilestoneButton ──────────────────────────────────────────────────────
// Toolbar icon on a task row: creates a new milestone for the task.

export function AddMilestoneButton({ taskId }: { taskId: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const { toast } = useToast();

  const createMutation = useMutation({
    mutationFn: async (t: string) => {
      const res = await apiRequest("POST", `/api/tasks/${taskId}/task-milestones`, { title: t });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "task-milestones"] });
      toast({ title: "Milestone created", description: "Use the + on the milestone to add sub-tasks to it." });
      setTitle("");
      setOpen(false);
    },
    onError: () => toast({ title: "Error", description: "Failed to create milestone.", variant: "destructive" }),
  });

  const submit = () => {
    if (createMutation.isPending) return;
    const t = title.trim();
    if (t) createMutation.mutate(t);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTitle(""); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title="New Milestone"
          data-testid={`button-add-milestone-${taskId}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Flag className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-2">
          <p className="text-sm font-medium flex items-center gap-1.5"><Flag className="h-3.5 w-3.5 text-indigo-500" />New Milestone</p>
          <Input
            autoFocus
            placeholder="Milestone name..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            data-testid={`input-add-milestone-${taskId}`}
          />
          <Button
            size="sm"
            className="w-full"
            onClick={submit}
            disabled={!title.trim() || createMutation.isPending}
            data-testid={`button-submit-add-milestone-${taskId}`}
          >
            {createMutation.isPending ? "Creating..." : "Create Milestone"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── InlineSubtaskPanel ──────────────────────────────────────────────────────

export function InlineSubtaskPanel({ taskId, subtasks, onToggle, onDateChange, onTextChange, onStar, onReorder, onDelete, hideCompleted, onToggleHideCompleted, readOnly }: {
  taskId: string;
  subtasks: SubTask[];
  /** True when the parent task is shared with the current user as view-only. */
  readOnly?: boolean;
  onToggle: (id: string) => void;
  onDateChange: (id: string, date: string | null) => void;
  onTextChange: (id: string, text: string) => void;
  onStar: (id: string) => void;
  onReorder: (taskId: string, reordered: SubTask[]) => void;
  /** Called when the user clicks delete on an inline sub-task row. */
  onDelete?: (id: string) => void;
  hideCompleted?: boolean;
  onToggleHideCompleted?: (taskId: string) => void;
}) {
  const [local, setLocal] = useState<SubTask[]>([]);
  const panelSensors = useSensors(
    // distance keeps simple taps (checkbox, text edit) from starting a drag,
    // while `touch-none` on the handle stops the browser from hijacking the
    // gesture as page scroll on coarse-pointer (touch) devices.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    setLocal([...subtasks].sort((a, b) => a.displayOrder - b.displayOrder));
  }, [subtasks]);

  // Structured (milestone / branching) view kicks in when any sub-task is
  // linked to a milestone, nested under another sub-task, or depends on one —
  // or when the task has milestones at all (so empty milestones are visible
  // and can receive their first sub-task).
  const { data: milestones = [] } = useQuery<TaskMilestoneLite[]>({
    queryKey: ["/api/tasks", taskId, "task-milestones"],
  });
  const hasStructure = milestones.length > 0 || subtasks.some(st => st.milestoneId || st.parentSubTaskId || st.dependsOnSubTaskId);
  const [addingToMilestoneId, setAddingToMilestoneId] = useState<string | null>(null);
  const [newMilestoneSubtaskText, setNewMilestoneSubtaskText] = useState("");
  const { toast } = useToast();
  const addMilestoneSubtaskMutation = useMutation({
    mutationFn: async ({ text, milestoneId }: { text: string; milestoneId: string }) => {
      const res = await apiRequest("POST", `/api/tasks/${taskId}/subtasks`, { text, milestoneId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "subtasks"] });
      setNewMilestoneSubtaskText("");
      setAddingToMilestoneId(null);
    },
    onError: () => toast({ title: "Error", description: "Failed to add sub-task.", variant: "destructive" }),
  });
  const submitMilestoneSubtask = (milestoneId: string) => {
    if (addMilestoneSubtaskMutation.isPending) return;
    const text = newMilestoneSubtaskText.trim();
    if (text) addMilestoneSubtaskMutation.mutate({ text, milestoneId });
  };
  const setDependencyMutation = useMutation({
    mutationFn: async ({ id, dependsOnId }: { id: string; dependsOnId: string | null }) => {
      const res = await apiRequest("PATCH", `/api/subtasks/${id}`, { dependsOnSubTaskId: dependsOnId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "subtasks"] });
    },
    onError: (err: any) => toast({
      title: "Can't set dependency",
      description: err?.message || "That dependency isn't allowed.",
      variant: "destructive",
    }),
  });
  const handleSetDependency = (id: string, dependsOnId: string | null) => {
    if (!setDependencyMutation.isPending) setDependencyMutation.mutate({ id, dependsOnId });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = local.findIndex(st => st.id === active.id);
    const newIndex = local.findIndex(st => st.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(local, oldIndex, newIndex);
    setLocal(reordered);
    onReorder(taskId, reordered);
  };

  // Render even with zero sub-tasks when the task has milestones, so an empty
  // milestone shows its 0/0 header and can receive its first sub-task.
  if (local.length === 0 && milestones.length === 0) return null;
  const completedCount = local.filter(st => st.completed).length;
  const visible = hideCompleted ? local.filter(st => !st.completed) : local;

  // ── Structured (branching) rows ──────────────────────────────────────────
  // Within each scope (general or one milestone) top-level rows are chained:
  // a row that depends on a sibling renders indented beneath it, and each
  // row's nested sub-sub-tasks render beneath it too.
  type TreeRow = { st: SubTask; depth: number; connector?: "child" | "dependency"; blockedByText?: string };
  const byId = new Map(local.map(st => [st.id, st]));
  const isHidden = (st: SubTask) => hideCompleted && st.completed;
  const blockedText = (st: SubTask) => {
    const dep = st.dependsOnSubTaskId ? byId.get(st.dependsOnSubTaskId) : undefined;
    return dep && !dep.completed ? `Waiting on: ${dep.text}` : undefined;
  };
  const appendChainedRows = (items: SubTask[], depth: number, connector: "child" | undefined, rows: TreeRow[], seen: Set<string>) => {
    // items = sibling rows at one level (top-level of a scope, or one row's children)
    const ids = new Set(items.map(s => s.id));
    const dependents = new Map<string, SubTask[]>();
    const roots: SubTask[] = [];
    for (const st of items) {
      if (st.dependsOnSubTaskId && ids.has(st.dependsOnSubTaskId)) {
        const list = dependents.get(st.dependsOnSubTaskId) ?? [];
        list.push(st);
        dependents.set(st.dependsOnSubTaskId, list);
      } else {
        roots.push(st);
      }
    }
    const markSubtreeSeen = (st: SubTask) => {
      for (const kid of local.filter(k => k.parentSubTaskId === st.id)) {
        if (seen.has(kid.id)) continue;
        seen.add(kid.id);
        markSubtreeSeen(kid);
      }
    };
    const visit = (st: SubTask, d: number, conn?: "child" | "dependency") => {
      if (seen.has(st.id)) return;
      seen.add(st.id);
      if (isHidden(st)) {
        // Hiding a completed row hides its whole nested branch, but rows that
        // depend on it are promoted to this level (no dangling connector).
        markSubtreeSeen(st);
        for (const dep of dependents.get(st.id) ?? []) visit(dep, d);
        return;
      }
      rows.push({ st, depth: d, connector: conn, blockedByText: blockedText(st) });
      // nested sub-sub-tasks (with their own dependency chains)
      appendChainedRows(local.filter(k => k.parentSubTaskId === st.id), d + 1, "child", rows, seen);
      // siblings that depend on this one
      for (const dep of dependents.get(st.id) ?? []) visit(dep, d + 1, "dependency");
    };
    for (const r of roots) visit(r, depth, connector);
    for (const st of items) visit(st, depth, connector); // safety: cycles/odd data
  };
  // Rows whose parent reference is missing or cyclic still need to render:
  // treat a row as top-level unless its parent actually exists in this list.
  const topLevel = local.filter(st => !st.parentSubTaskId || !byId.has(st.parentSubTaskId));
  const milestoneIdsInUse = new Set(local.filter(st => st.milestoneId).map(st => st.milestoneId as string));
  const orderedMilestones = [
    // every milestone on the task (empty ones too, so they can receive sub-tasks)
    ...milestones,
    // milestones referenced by sub-tasks but not (yet) in the fetched list
    ...[...milestoneIdsInUse].filter(id => !milestones.some(m => m.id === id)).map(id => ({ id, title: "Milestone" }) as TaskMilestoneLite),
  ];

  // Build every scope with ONE shared seen set so no row is ever dropped or
  // rendered twice, then sweep up anything unreached (cyclic/odd legacy data).
  const sharedSeen = new Set<string>();
  const generalRows: TreeRow[] = [];
  appendChainedRows(topLevel.filter(st => !st.milestoneId), 0, undefined, generalRows, sharedSeen);
  const milestoneGroups = orderedMilestones.map(m => {
    const rows: TreeRow[] = [];
    appendChainedRows(topLevel.filter(st => st.milestoneId === m.id), 0, undefined, rows, sharedSeen);
    const all = local.filter(st => st.milestoneId === m.id);
    return { m, rows, done: all.filter(st => st.completed).length, total: all.length };
  });
  // Fallback: anything not visited (e.g. parent cycles in legacy data)
  const leftoverRows: TreeRow[] = local
    .filter(st => !sharedSeen.has(st.id) && !isHidden(st))
    .map(st => ({ st, depth: 0, blockedByText: blockedText(st) }));
  // Eligible dependency targets for a row: siblings at the same level (same
  // milestone scope and same parent), excluding itself and its descendants.
  const descendantIds = (rootId: string): Set<string> => {
    const out = new Set<string>();
    const walk = (id: string) => {
      for (const kid of local.filter(k => k.parentSubTaskId === id)) {
        if (out.has(kid.id)) continue;
        out.add(kid.id);
        walk(kid.id);
      }
    };
    walk(rootId);
    return out;
  };
  const dependencyOptionsFor = (st: SubTask): { id: string; text: string }[] => {
    const excluded = descendantIds(st.id);
    return local
      .filter(s =>
        s.id !== st.id &&
        !excluded.has(s.id) &&
        (s.milestoneId ?? null) === (st.milestoneId ?? null) &&
        (s.parentSubTaskId ?? null) === (st.parentSubTaskId ?? null))
      .map(s => ({ id: s.id, text: s.text }));
  };
  const renderRow = (row: TreeRow) => (
    <SortableInlineSubTaskItem
      key={row.st.id}
      subtask={row.st}
      onToggle={onToggle}
      onDateChange={onDateChange}
      onTextChange={onTextChange}
      onStar={onStar}
      onDelete={onDelete}
      readOnly={readOnly}
      depth={row.depth}
      connector={row.connector}
      blockedByText={row.blockedByText}
      dragDisabled
      dependencyOptions={dependencyOptionsFor(row.st)}
      onSetDependency={handleSetDependency}
      subtaskIsOverdue={!row.st.completed && !!row.st.dueDate && isPast(new Date(row.st.dueDate))}
    />
  );

  return (
    <div className="mt-2 pt-2 border-t border-border/50" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between pl-8 pr-1 mb-1.5">
        <div className="flex items-center gap-1.5">
          <ListTodo className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground font-medium">Sub-tasks ({completedCount}/{local.length})</span>
        </div>
        {completedCount > 0 && onToggleHideCompleted && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleHideCompleted(taskId); }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-1 py-0.5 rounded hover:bg-muted"
            title={hideCompleted ? "Show completed sub-tasks" : "Hide completed sub-tasks"}
          >
            {hideCompleted ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            <span>{hideCompleted ? `${completedCount} hidden` : "Hide done"}</span>
          </button>
        )}
      </div>
      {hasStructure ? (
        <DndContext sensors={panelSensors} collisionDetection={closestCenter}>
          <SortableContext items={local.map(st => st.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-0.5">
              {generalRows.map(renderRow)}
              {milestoneGroups.map(({ m, rows, done, total }) => {
                return (
                  <div key={m.id} data-testid={`milestone-subtask-group-${m.id}`}>
                    <div className="flex items-center gap-1.5 pl-8 pr-2 pt-2 pb-0.5 group/mh">
                      <Flag className="h-3 w-3 text-indigo-500 flex-shrink-0" />
                      <span className={`text-xs font-medium ${m.completed ? "line-through text-muted-foreground" : "text-foreground/80"}`}>{m.title}</span>
                      <span className="text-[10px] text-muted-foreground">({done}/{total})</span>
                      {!readOnly && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 ml-auto text-muted-foreground hover:text-indigo-600"
                          title={`Add sub-task to "${m.title}"`}
                          data-testid={`button-add-subtask-milestone-${m.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setNewMilestoneSubtaskText("");
                            setAddingToMilestoneId(prev => (prev === m.id ? null : m.id));
                          }}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="ml-3 border-l border-border/60 pl-1 space-y-0.5">
                      {rows.map(renderRow)}
                      {addingToMilestoneId === m.id && !readOnly && (
                        <div className="flex items-center gap-2 pl-8 pr-2 py-1">
                          <Input
                            autoFocus
                            className="h-7 text-sm"
                            placeholder="New sub-task..."
                            value={newMilestoneSubtaskText}
                            onChange={(e) => setNewMilestoneSubtaskText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); submitMilestoneSubtask(m.id); }
                              if (e.key === "Escape") { setAddingToMilestoneId(null); setNewMilestoneSubtaskText(""); }
                            }}
                            data-testid={`input-add-subtask-milestone-${m.id}`}
                          />
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={!newMilestoneSubtaskText.trim() || addMilestoneSubtaskMutation.isPending}
                            onClick={() => submitMilestoneSubtask(m.id)}
                            data-testid={`button-submit-add-subtask-milestone-${m.id}`}
                          >
                            {addMilestoneSubtaskMutation.isPending ? "Adding..." : "Add"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {leftoverRows.map(renderRow)}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <DndContext sensors={panelSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visible.map(st => st.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-0.5">
              {visible.map(st => (
                <SortableInlineSubTaskItem
                  key={st.id}
                  subtask={st}
                  onToggle={onToggle}
                  onDateChange={onDateChange}
                  onTextChange={onTextChange}
                  onStar={onStar}
                  onDelete={onDelete}
                  readOnly={readOnly}
                  dependencyOptions={dependencyOptionsFor(st)}
                  onSetDependency={handleSetDependency}
                  subtaskIsOverdue={!st.completed && !!st.dueDate && isPast(new Date(st.dueDate))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
